import { update } from 'lodash/fp';
import { MAX_LEVEL } from 'lib/constants';
import PartList from 'lib/part-list';
import { DATA, LOAD, LOAD_HELICAL, LOAD_PUSH_PIER } from 'reducers/projects/defaults';
import compatibleLead from './make-pile-consistent';

const getShaftLength = (lead) => _.ceil(lead?.shaftLength || (lead?.length / 12));

const clampLength = (pileLength, aboveGrade, lead) => {
  const gradeOffset = Math.max(-(aboveGrade ?? 0), 0);
  const minLength = getShaftLength(lead) + gradeOffset;
  return _.clamp(pileLength, minLength, MAX_LEVEL);
}

const pileUpdate = (load, action) => {
  if (load.pileType !== 'helical') return load;

  const pileOptions = {...(load.pileOptions || {}), ...(action.filters || {})};
  const lead = action.lead || _.first(load?.pileSegments);
  pileOptions.pileLength = clampLength(pileOptions.pileLength, load.aboveGrade, lead);
  const newLead = compatibleLead(lead, pileOptions, action.products);
  const pileSegments = newLead ? PartList.generatePile(newLead, pileOptions.pileLength) : [];
  return {...load, pileOptions, pileSegments};
}

function loads(state=DATA.loads, action) {
  switch(action.type) {
    case 'load/add':
      return [...state, {...LOAD, id: action.id || _.uuid()}];
    case 'load/remove':
      return _.reject(state, {id: action.id});
    case 'load/update':
      return _.map(state, (load) => {
        if (load.id !== action.id) return load;

        if (action.load.pileType && load.pileType && load.pileType != action.load.pileType) {
          const defaults = action.load.pileType === 'helical' ? LOAD_HELICAL : LOAD_PUSH_PIER;
          const removeKeys = _.keys(action.load.pileType === 'helical' ? LOAD_PUSH_PIER : LOAD_HELICAL);
          load = {..._.omit(load, removeKeys), ...defaults};
        }

        const updatedLoad = {...load, ...action.load};
        if (action.load.product) {
          updatedLoad.pileType = "push_pier";
        }
        const minPileLength = (getShaftLength(_.first(load?.pileSegments)) - load.pileOptions?.pileLength) || - load.depth + 5
        updatedLoad.aboveGrade = Math.max(updatedLoad.aboveGrade, minPileLength);
        return updatedLoad;
      });
    case 'load/pile/update':
      return _.map(state, (load) => {
        if (load.id !== action.loadId) return load;

        load = _.omit({...load, pileType: 'helical'}, 'product', 'ram');
        return pileUpdate(load, action);
      });
    case 'products/received':
      return _.map(state, (load) => pileUpdate(load, action));
    default:
      return state;
  }
}

function blowCounts(state=DATA.blowCounts, action) {
  switch(action.type) {
    case 'RECEIVED_NEW_BLOW_COUNTS':
      return action.blowCounts;
    case 'SOIL_RESET':
      return DATA.blowCounts;
    default:
      return state;
  }
}

function blowCountsSD(state=DATA.blowCountsSD, action) {
  switch(action.type) {
    case 'SET_BLOW_COUNT_SD':
      return action.blowCountsSD;
    case 'SOIL_RESET':
      return DATA.blowCountsSD;
    default:
      return state;
  }
}

function boundaries(state=DATA.boundaries, {boundaries, ...action}) {
  switch(action.type) {
    case 'SET_BOUNDARIES':
      return boundaries;
    case 'SOIL_RESET':
      return DATA.boundaries;
    default:
      return state;
  }
}

function soilTypes(state=DATA.soilTypes, action) {
  switch(action.type) {
    case 'SET_SOIL_TYPES':
      return action.soils;
    case 'SOIL_RESET':
      return DATA.soilTypes;
    default:
      return state;
  }
}

function bulkSoil(state=DATA.bulkSoil, action) {
  switch(action.type) {
    case 'SET_BULK_SOIL':
      return action.bulkSoil;
    case 'SET_SOIL_TYPES':
    case 'SET_BOUNDARIES':
    case 'RECEIVED_NEW_BLOW_COUNTS':
    case 'SOIL_RESET':
      return DATA.bulkSoil;
    default:
      return state;
  }
}

function logs(state, action) {
  switch (action.type) {
    case 'logs/update':
      return {...(state || {}), ...action.logs};
    case 'logs/update/pile':
      const {loadId, pileNumber, log} = action;
      return update(['loads', loadId, 'piles', pileNumber], (l) => ({...l, ...log}), state);
    case 'logs/reset/pile':
      return update(['loads', action.loadId, 'piles', action.pileNumber], (l) => null, state);
    default:
      return state;
  }
}

export default function data(state=DATA, action) {
  return {
    ...state,
    loads: loads(state?.loads, action),
    blowCounts: blowCounts(state.blowCounts, action),
    blowCountsSD: blowCountsSD(state.blowCountsSD, action),
    boundaries: boundaries(state.boundaries, action),
    soilTypes: soilTypes(state.soilTypes, action),
    bulkSoil: bulkSoil(state.bulkSoil, action),
    logs: logs(state.logs, action)
  };
}
